import { isArray, isString } from '@dimjs/lang';
import { extend } from '@dimjs/utils';
import { isUndefinedOrNull, trim } from '@flatbiz/utils';
import { utils } from '@wove/react';
import {
  FieldItemProps,
  FieldRegExpRule,
  FieldRequiredRule,
  FieldsData,
  FieldValue,
  FormErrorItem,
  FormMethods,
  RuleItem,
} from '../types';
import { getFormModalPickerValue, getFormModalValue } from './utils';

type RegisterFieldItem = {
  props: FieldItemProps;
  formItemValidate: (errorList?: FormErrorItem[]) => void;
  onStoreValueChange: () => void;
  onScrollIntoView: (name: string) => void;
  clearValidateMessage: () => void;
  // deleteFormItemValue: () => void;
};

export interface FormMethodsInner extends FormMethods {
  registerField: (field: RegisterFieldItem) => () => void;
  setFieldsValueInner: (data: FieldsData) => void;
  getValidateErrorList: (names?: string[]) => FormErrorItem[];
  setFieldNameList: (name: string[]) => void;
  setInitialValues: (initialValues?: FieldsData) => void;
  dependenciesFormItemValidate: (name: string) => void;
}

export class FormStore {
  private store = {} as Record<string, FieldValue>;
  private fieldNameList = [] as string[];
  private fieldsMap = {} as Record<string, RegisterFieldItem>;
  private initialValues?: FieldsData;

  private getFieldValue = (name: string) => {
    return this.store[name];
  };

  private getFieldsValue = () => {
    return this.store;
  };

  private setFieldNameList = (names: string[]) => {
    this.fieldNameList = names;
  };

  // 注册组件实例，返回一个回调函数，用于删除组件实例
  private registerField = (field: RegisterFieldItem) => {
    const name = field.props.name;
    this.fieldsMap[name] = field;
    return () => {
      this.fieldNameList = this.fieldNameList.filter((item) => item !== name);
      delete this.store[name];
      delete this.fieldsMap[name];
    };
  };

  /**
   * 使用此方法后，输入框将通过受控的方式修改值
   * 使用场景（非输入框输入场景）
   *  1.外部主动设置值
   *  2.点击删除按钮
   * @param newStore
   */
  private setFieldsValue = (newStore: FieldsData) => {
    const newStoreValues = {} as Record<string, FieldValue>;
    Object.keys(newStore).forEach((key) => {
      const value = newStore[key] === '' || newStore[key] === null ? undefined : newStore[key];
      newStoreValues[key] = value;
    });

    const previous = { ...this.store };
    this.store = {
      ...this.store,
      ...newStoreValues,
    };

    // store已经更新，但是我们希望组件也跟着更新
    Object.keys(newStoreValues).forEach((key) => {
      // 如果上一组数据和最新数据都是相等，则不做任何操作
      if (!utils.deepEqual(previous[key], this.store[key])) {
        this.fieldsMap[key]?.onStoreValueChange();
      }
    });
  };

  private setFieldsValueInner = (newStore: FieldsData) => {
    this.store = {
      ...this.store,
      ...newStore,
    };
  };

  private parseRule = (ruleItem: RuleItem, name: string, value: FieldValue): FormErrorItem | null => {
    const FieldRequiredrule = ruleItem as FieldRequiredRule;
    if (FieldRequiredrule.required && (value === undefined || value === '' || value === null)) {
      return { message: FieldRequiredrule.message, name, value, type: 'required' };
    }
    if (typeof ruleItem === 'function') {
      const result = ruleItem(this.store);
      if (!result.status) {
        return { message: result.message, name, value, type: 'custom' };
      }
    } else {
      if (typeof value === 'string') {
        const FieldRegExprule = ruleItem as FieldRegExpRule;
        const regexpDf = FieldRegExprule.reg;
        if (!regexpDf) return null;
        const result = regexpDf.test(value);
        if (!result) {
          return { message: FieldRegExprule.message, name, value, type: 'regExp' };
        }
      }
    }
    return null;
  };

  // 表单所有字段校验
  private validateAllFields = (names: string[]): FormErrorItem[] => {
    const err = [] as FormErrorItem[];
    names.forEach((key) => {
      const { name, rules } = this.fieldsMap[key]?.props || {};
      if (!name) return;
      const storeItemValue = this.getFieldValue(name);
      const value = isString(storeItemValue) ? trim(storeItemValue as string, true) : storeItemValue;

      if (rules && isArray(rules)) {
        // const requiredTarget = rules.find((ruleItem) => ruleItem['required'] === true);
        // if (!requiredTarget && (value === undefined || value === '' || value === null)) {
        //   return;
        // }
        for (let index = 0; index < rules.length; index++) {
          const ruleItem = rules[index];
          const temp = this.parseRule(ruleItem, name, value);
          if (temp) err.push(temp);
        }
      }
    });
    return err;
  };
  private getValidateErrorList = (names?: string[]) => {
    return this.validateAllFields(names || this.fieldNameList);
  };

  private validateFields = (names?: string[]): FormErrorItem[] => {
    const allErrorList = this.validateAllFields(names || this.fieldNameList);
    allErrorList.forEach((item) => {
      this.fieldsMap[item.name]?.formItemValidate?.();
    });
    if (allErrorList.length > 0) {
      const scrollName = allErrorList[0].name;
      this.fieldsMap[scrollName]?.onScrollIntoView?.(scrollName);
    }
    return allErrorList;
  };
  private validateFieldsRequired = (names?: string[]): boolean => {
    const allErrorList = this.validateAllFields(names || this.fieldNameList);
    for (let index = 0; index < allErrorList.length; index++) {
      const element = allErrorList[index];
      if (element.type === 'required') {
        return true;
      }
    }
    return false;
  };

  private validateField = (name: string): FormErrorItem[] => {
    return this.validateFields([name]);
  };

  private clearValidateMessage = (names?: string[]) => {
    const nameList = names || this.fieldNameList;
    nameList?.map((name) => {
      this.fieldsMap[name]?.clearValidateMessage();
    });
  };
  private onReset = (values?: FieldsData) => {
    Object.keys(this.store).forEach((name) => {
      this.setFieldsValue({ [name]: undefined });
    });
    this.store = {};
    this.setFieldsValue(extend({}, this.initialValues, values));
  };
  private setInitialValues = (initialValues) => {
    this.initialValues = initialValues;
  };

  /**
   * dependencies校验，不触发required校验
   * @param name
   */
  private dependenciesFormItemValidate = (name: string) => {
    try {
      if (this.fieldsMap[name]) {
        const storeItemValue = this.getFieldValue(name);
        const value = isString(storeItemValue) ? trim(storeItemValue as string, true) : storeItemValue;
        const err = [] as FormErrorItem[];
        const rules = this.fieldsMap[name]?.props?.rules || [];
        for (let index = 0; index < rules.length; index++) {
          if (!isUndefinedOrNull(rules[index]['required'])) {
            continue;
          }
          const temp = this.parseRule(rules[index], name, value);
          if (temp) err.push(temp);
        }
        this.fieldsMap[name]?.formItemValidate?.(err);
      }
    } catch (error) {
      console.error(`form item name:${name} 不存在`);
    }
  };

  public getForm(): FormMethods {
    return {
      getFieldValue: this.getFieldValue,
      getFieldsValue: this.getFieldsValue,
      setFieldsValue: this.setFieldsValue,
      validateFields: this.validateFields,
      validateField: this.validateField,
      validateFieldsRequired: this.validateFieldsRequired,
      clearValidateMessage: this.clearValidateMessage,
      onReset: this.onReset,
      getFormModalPickerValue: getFormModalPickerValue,
      getFormModalValue: getFormModalValue,
      _this: this,
    };
  }

  public getFormInner(): FormMethodsInner {
    return {
      registerField: this.registerField,
      getFieldValue: this.getFieldValue,
      getFieldsValue: this.getFieldsValue,
      setFieldsValue: this.setFieldsValue,
      validateFields: this.validateFields,
      validateField: this.validateField,
      setFieldsValueInner: this.setFieldsValueInner,
      getValidateErrorList: this.getValidateErrorList,
      validateFieldsRequired: this.validateFieldsRequired,
      setFieldNameList: this.setFieldNameList,
      clearValidateMessage: this.clearValidateMessage,
      onReset: this.onReset,
      setInitialValues: this.setInitialValues,
      getFormModalPickerValue: getFormModalPickerValue,
      getFormModalValue: getFormModalValue,
      dependenciesFormItemValidate: this.dependenciesFormItemValidate,
      _this: this,
    };
  }
}
